import type { Company } from "@dutyon/apis";
import { Box, Button, Divider, Group, PasswordInput, TextInput } from "@dutyon/ui-kit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const baseSchema = z.object({
  name: z
    .string()
    .min(1, { message: "병원 이름을 입력해주세요" })
    .max(100, { message: "병원 이름은 100자 이하로 입력해주세요" }),
  code: z
    .string()
    .min(2, { message: "병원 코드는 2자 이상 입력해주세요" })
    .max(50, { message: "병원 코드는 50자 이하로 입력해주세요" })
    .regex(/^[A-Za-z0-9_-]+$/, {
      message: "병원 코드는 영문, 숫자, 밑줄과 하이픈만 사용할 수 있습니다",
    }),
});

const createSchema = baseSchema.extend({
  adminEmail: z.string().email({ message: "올바른 이메일 형식이 아닙니다" }),
  adminName: z
    .string()
    .min(1, { message: "관리자 이름을 입력해주세요" })
    .max(100, { message: "관리자 이름은 100자 이하로 입력해주세요" }),
  adminPassword: z
    .string()
    .min(6, { message: "비밀번호는 최소 6자 이상이어야 합니다" })
    .max(128, { message: "비밀번호는 128자 이하로 입력해주세요" }),
});

// Admin fields are optional in the generic type to allow both schemas to work
export type CompanyFormData = z.infer<typeof baseSchema> & {
  adminEmail?: string;
  adminName?: string;
  adminPassword?: string;
} & Partial<Company>;

interface CompanyFormProps {
  initialValues?: Partial<CompanyFormData>;
  onSubmit: (values: CompanyFormData) => void;
  isLoading?: boolean;
}

export function CompanyForm({ initialValues, onSubmit, isLoading }: CompanyFormProps) {
  const isNewCompany = !initialValues?.id;
  const schema = isNewCompany ? createSchema : baseSchema;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyFormData>({
    defaultValues: initialValues || {},
    resolver: zodResolver(schema),
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <TextInput
        label="병원 이름"
        placeholder="병원 이름을 입력하세요"
        required
        mb="md"
        {...register("name")}
        error={errors.name?.message}
      />
      <TextInput
        label="병원 코드"
        placeholder="병원 코드를 입력하세요 (예: CODE001)"
        required
        mb="md"
        {...register("code")}
        error={errors.code?.message}
      />

      {isNewCompany && (
        <>
          <Divider my="xl" label="병원 관리자 계정 정보" labelPosition="center" />
          <TextInput
            label="관리자 이메일"
            placeholder="관리자 이메일을 입력하세요"
            type="email"
            required
            mb="md"
            {...register("adminEmail")}
            error={errors.adminEmail?.message}
          />
          <TextInput
            label="관리자 이름"
            placeholder="관리자 이름을 입력하세요"
            required
            mb="md"
            {...register("adminName")}
            error={errors.adminName?.message}
          />
          <PasswordInput
            label="관리자 초기 비밀번호"
            placeholder="초기 비밀번호를 입력하세요 (최소 6자)"
            required
            mb="md"
            {...register("adminPassword")}
            error={errors.adminPassword?.message}
          />
        </>
      )}

      <Group justify="flex-end">
        <Button type="submit" loading={isLoading}>
          제출
        </Button>
      </Group>
    </Box>
  );
}
